/** Extraction runs and review items (spec §10.4, §8.11). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api/client";
import type { ExtractionRun, ResolveResult, ReviewPage } from "../api/types";

export const reviewKeys = {
  list: (ws: string, kind: string, status: string) => ["review", ws, kind, status] as const,
  all: (ws: string) => ["review", ws] as const,
  run: (id: string) => ["extractionRun", id] as const,
};

export type ReviewAction = "promote" | "reject" | "choose_candidate" | "create_concept" | "pick_version" | "apply_migration" | "dismiss" | "revert";

export function useReviewItems(workspaceId: string, kind: string, status: string) {
  return useQuery({
    queryKey: reviewKeys.list(workspaceId, kind, status),
    staleTime: 5_000,
    queryFn: async () =>
      unwrap<ReviewPage>(
        await api.GET("/api/workspaces/{ws_id}/review", {
          params: { path: { ws_id: workspaceId }, query: { kind, status, limit: 100 } },
        }),
      ),
  });
}

export function useResolveItem(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; action: ReviewAction; payload?: Record<string, unknown> }) =>
      unwrap<ResolveResult>(
        await api.POST("/api/review/{item_id}/resolve", {
          params: { path: { item_id: v.id } },
          body: { action: v.action, payload: v.payload ?? {} },
        }),
      ),
    // promotion is queued on the loro_writes worker; refetch shortly after to show it resolved
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: reviewKeys.all(workspaceId) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: reviewKeys.all(workspaceId) }), 1500);
    },
  });
}

export async function startRun(workspaceId: string, docIds: string[]): Promise<ExtractionRun> {
  return unwrap<ExtractionRun>(await api.POST("/api/extraction/runs", { body: { workspaceId, scope: { docIds, treeIds: [] } } }));
}

export interface BlockIssue {
  id: string;
  kind: string;
}

/** Open review items of one page, grouped by block (spec §8.6: conflict badge). Online only. */
export function useDocIssues(workspaceId: string, docId: string) {
  return useQuery({
    queryKey: ["reviewDoc", workspaceId, docId],
    staleTime: 10_000,
    refetchInterval: 30_000,
    retry: false,
    queryFn: async () => {
      const page = unwrap<ReviewPage>(
        await api.GET("/api/workspaces/{ws_id}/review", {
          params: { path: { ws_id: workspaceId }, query: { docId, status: "open", limit: 200 } },
        }),
      );
      const byBlock = new Map<string, BlockIssue[]>();
      for (const item of page.items) {
        if (!item.treeId) continue;
        byBlock.set(item.treeId, [...(byBlock.get(item.treeId) ?? []), { id: item.id, kind: item.kind }]);
      }
      return byBlock;
    },
  });
}

export function useRun(runId: string | null) {
  return useQuery({
    queryKey: reviewKeys.run(runId ?? ""),
    enabled: !!runId,
    queryFn: async () => unwrap<ExtractionRun>(await api.GET("/api/extraction/runs/{run_id}", { params: { path: { run_id: runId! } } })),
    refetchInterval: (q) => (q.state.data && ["done", "failed"].includes(q.state.data.status) ? false : 1000),
  });
}

/** `urn:km:blk:<doc>:<tree>` → route params, for "open block" links. */
export function blockTarget(iri: string): { docId: string; treeId: string } | null {
  const m = /^urn:km:blk:([^:#]+):([^#]+)/.exec(iri);
  return m ? { docId: m[1]!, treeId: m[2]! } : null;
}
